import { Box, Button, Flex, Group, SimpleGrid, Stack, Text } from '@mantine/core';
import { createFileRoute } from '@tanstack/react-router';
import type { ImageListBody } from '@wwaave/shared/cms';
import type { ReactNode } from 'react';

import { MarketingHero } from '../components/marketing/MarketingHero';
import { MarketingNextSteps } from '../components/marketing/MarketingNextSteps';
import { MarketingSection } from '../components/marketing/MarketingSection';
import { PublicShell } from '../components/marketing/PublicShell';
import { JoinLink } from '../components/ui/JoinLink';
import { useCmsImage, useCmsImageList, useCmsSlot } from '../lib/cms';
import { CTA_CLAY } from '../lib/cta-clay';

export const Route = createFileRoute('/our-story')({
  component: AboutPage,
});

function AboutPage() {
  const heroEyebrow = useCmsSlot<'markdown'>('about.hero_eyebrow');
  const heroTitle = useCmsSlot<'markdown'>('about.hero_title');
  const heroLede = useCmsSlot<'markdown'>('about.hero_lede');
  const originStory = useCmsSlot<'bullet_list'>('about.origin_story');
  const luangPrabang = useCmsSlot<'bullet_list'>('about.luang_prabang');
  // The bordered "From a small ripple…" card that used to close this section
  // was removed 2026-08-18 at Richie's request (feedback tSkTYyeLO6oUl2W3v4kHM).
  // Its two slots (about.ripple_quote / about.ripple_body) are deprecated in the
  // registry rather than deleted — prod holds overrides on them.
  const whyExists = useCmsSlot<'bullet_list'>('about.why_exists');
  const originEyebrow = useCmsSlot<'markdown'>('about.origin_eyebrow');
  const originTitle = useCmsSlot<'markdown'>('about.origin_title');
  const originQuote = useCmsSlot<'markdown'>('about.origin_quote');
  const luangEyebrow = useCmsSlot<'markdown'>('about.luang_eyebrow');
  const luangTitle = useCmsSlot<'markdown'>('about.luang_title');
  const luangPrabangQuote = useCmsSlot<'markdown'>('about.luang_prabang_quote');
  const whyEyebrow = useCmsSlot<'markdown'>('about.why_eyebrow');
  const whyTitle = useCmsSlot<'markdown'>('about.why_title');
  const ctaArtistLabel = useCmsSlot<'markdown'>('about.cta_artist');
  const ctaHostLabel = useCmsSlot<'markdown'>('about.cta_host');
  const nextStepsTitle = useCmsSlot<'markdown'>('about.next_steps_title');
  const { image: heroImg, ready: heroImgReady } = useCmsImage('about.hero_image');
  const originImages = useCmsImageList('about.origin_images');
  const luangImages = useCmsImageList('about.luang_images');
  const whyImages = useCmsImageList('about.why_images');

  return (
    <PublicShell>
      <MarketingHero
        eyebrow={heroEyebrow}
        title={heroTitle}
        lede={heroLede}
        {...(heroImgReady ? { backgroundImage: heroImg.url } : {})}
        backgroundImageAlt={heroImg.alt}
        backgroundImageOpacity={heroImg.opacity ?? 0.2}
      />

      <MarketingSection
        eyebrow={originEyebrow}
        title={originTitle}
      >
        <StorySection
          paragraphs={originStory}
          images={originImages.images}
          ready={originImages.ready}
          quote={<PullQuote>{originQuote}</PullQuote>}
        />
      </MarketingSection>

      <MarketingSection
        eyebrow={luangEyebrow}
        title={luangTitle}
        tinted
      >
        <StorySection
          paragraphs={luangPrabang}
          images={luangImages.images}
          ready={luangImages.ready}
          quote={<PullQuote>{luangPrabangQuote}</PullQuote>}
        />
      </MarketingSection>

      <MarketingSection
        eyebrow={whyEyebrow}
        title={whyTitle}
      >
        <Stack gap="xl">
          <StorySection paragraphs={whyExists} images={whyImages.images} ready={whyImages.ready} />
          <Group gap="md" justify="center">
            <JoinLink audience="artist">
              {/* Solid Clay, matching the homepage hero / closing artist CTAs.
                  `brand.6` IS Clay #D2602E, so an admin branding override still
                  flows through, and the label colour is left to the theme's
                  autoContrast rather than hardcoded. */}
              <Button
                component="span"
                tabIndex={-1}
                size="lg"
                variant="filled"
                color={CTA_CLAY}
                styles={{ root: { minWidth: 220 } }}
              >
                {ctaArtistLabel}
              </Button>
            </JoinLink>
            <JoinLink audience="host">
              {/* Venue CTAs are Tide teal site-wide, artist CTAs stay Clay — the
                  two audiences read as two paths at a glance. Matches the
                  homepage hero/closing split, /how-it-works and
                  /founding-venues. */}
              <Button
                component="span"
                tabIndex={-1}
                size="lg"
                variant="gradient"
                gradient={{ from: 'tide.7', to: 'tide.6', deg: 35 }}
                styles={{ root: { minWidth: 220 } }}
              >
                {ctaHostLabel}
              </Button>
            </JoinLink>
          </Group>
        </Stack>
      </MarketingSection>

      <MarketingNextSteps
        title={nextStepsTitle}
        destinations={['/community-guidelines', '/our-ethos', '/ripples']}
      />
    </PublicShell>
  );
}

/**
 * StorySection — a section's paragraphs, each paired with the photo at the
 * same position in its image list and floated to alternating sides (photo 1
 * right, photo 2 left, and so on) so the section reads as text-and-image
 * rather than a wall of type. A paragraph with no photo at its index renders
 * full width, so the page is unchanged until an admin uploads. Any photos
 * beyond the paragraph count fall through to `StoryGallery` below the prose.
 */
function StorySection({
  paragraphs,
  images,
  ready,
  quote,
}: {
  paragraphs: readonly string[];
  images: ImageListBody;
  ready: boolean;
  quote?: ReactNode;
}) {
  const paired = ready ? images : [];
  const leftover = paired.slice(paragraphs.length);
  return (
    <Stack gap="xl">
      {paragraphs.map((para, i) => {
        const image = paired[i];
        const text = (
          <Text size="lg" style={{ lineHeight: 1.7, flex: 1 }}>
            {para}
          </Text>
        );
        if (!image) return <Box key={i}>{text}</Box>;
        const side: 'left' | 'right' = i % 2 === 0 ? 'right' : 'left';
        return (
          <Flex
            key={i}
            direction={{ base: 'column', md: side === 'left' ? 'row-reverse' : 'row' }}
            gap={{ base: 'md', md: 'xl' }}
            align="flex-start"
          >
            {text}
            <StoryImage image={image} />
          </Flex>
        );
      })}
      {quote}
      {leftover.length > 0 ? <StoryGallery images={leftover} ready mt="sm" /> : null}
    </Stack>
  );
}

/**
 * StoryImage — one paragraph-side photo within `StorySection`. Fixed width
 * on desktop (large enough to read as a real photo, not a thumbnail) so it
 * sits beside the text; full width on mobile, stacked above the paragraph.
 */
function StoryImage({ image }: { image: ImageListBody[number] }) {
  return (
    <Box component="figure" m={0} w={{ base: '100%', md: 320, lg: 380 }} style={{ flexShrink: 0 }}>
      <img
        src={image.url}
        alt={image.alt}
        loading="lazy"
        decoding="async"
        style={{
          display: 'block',
          width: '100%',
          height: 'auto',
          borderRadius: 'var(--mantine-radius-md)',
          border: '1px solid var(--wwaave-warmth-border)',
        }}
      />
      {image.caption !== undefined && image.caption !== '' ? (
        <Text component="figcaption" size="xs" c="ink.6" mt={4} style={{ lineHeight: 1.4 }}>
          {image.caption}
        </Text>
      ) : null}
    </Box>
  );
}

/**
 * StoryGallery — photos that didn't pair with a paragraph in `StorySection`
 * (more photos uploaded than there are paragraphs). One photo runs full
 * width; two or more sit side by side on desktop and stack on mobile.
 *
 * `ready` gates the render the same way the hero image does, so an admin-curated
 * gallery never paints in late over settled prose.
 *
 * Photos are `<img>` with lazy/async hints — never background-image — and each
 * carries the admin's alt text. A caption, if set, renders under the photo.
 */
function StoryGallery({
  images,
  ready,
  mt,
}: {
  images: ImageListBody;
  ready: boolean;
  mt?: string;
}) {
  if (!ready || images.length === 0) return null;
  return (
    <SimpleGrid cols={{ base: 1, sm: images.length === 1 ? 1 : 2 }} spacing="md" mt={mt ?? 0}>
      {images.map((img, i) => (
        <Box key={`${img.url}-${i}`} component="figure" m={0}>
          <img
            src={img.url}
            alt={img.alt}
            loading="lazy"
            decoding="async"
            style={{
              display: 'block',
              width: '100%',
              height: 'auto',
              borderRadius: 'var(--mantine-radius-md)',
              border: '1px solid var(--wwaave-warmth-border)',
            }}
          />
          {img.caption !== undefined && img.caption !== '' ? (
            <Text component="figcaption" size="sm" c="ink.6" mt={8} style={{ lineHeight: 1.5 }}>
              {img.caption}
            </Text>
          ) : null}
        </Box>
      ))}
    </SimpleGrid>
  );
}

function PullQuote({ children }: { children: ReactNode }) {
  return (
    <Box
      component="blockquote"
      my="xs"
      style={{
        margin: 0,
        paddingLeft: 20,
        paddingBlock: 12,
        borderLeft: '3px solid var(--mantine-color-brand-6)',
        fontStyle: 'italic',
        fontSize: '1.25rem',
        lineHeight: 1.45,
        color: 'var(--mantine-color-ink-9)',
        background: 'var(--wwaave-blockquote-bg)',
      }}
    >
      {children}
    </Box>
  );
}
